// schema
import * as ISchema from "#webapp/src/__schema__";

import { logger } from "#webapp/src/util/logger.util";
import { ZonedMoment } from "@clockwise/web-commons/src/util/time-zone.util";
import { requestSubscription } from "react-relay";
import { Environment, RecordProxy } from "relay-runtime";
import { subscription } from "./UpdateEventParentBaseWrappers.gql";

export function updateEventParentBaseWrappers(
  environment: Environment,
  input: ISchema.IUpdateEventParentBaseWrappersSubscriptionInput,
) {
  if (!(environment instanceof Environment)) {
    // don't proceed if it's not a modern environment
    return;
  }

  const variables = {
    input,
  };

  return requestSubscription(environment, {
    subscription,
    variables,
    // optional but recommended:
    onCompleted: () =>
      logger.debug("Subscription request for UpdateEventParentBaseWrappers complete"),
    onError: (error: Error) =>
      logger.error("Subscription request for UpdateEventParentBaseWrappers failed", error),
    updater: (store: any) => {
      const subscriptionField: RecordProxy = store.getRootField("updateEventParentBaseWrapper");
      const orgProxy: RecordProxy = store.get(input.orgRelayId);

      if (!subscriptionField || !orgProxy) {
        return;
      }

      const weekDayMap: { [key: string]: Set<RecordProxy>[] } = {};
      input.dates.forEach((weekStart) => {
        weekDayMap[weekStart] = [];

        for (let i = 0; i < 7; i++) {
          weekDayMap[weekStart].push(new Set());
        }
      });

      (subscriptionField.getLinkedRecords("eventParents") || []).forEach(
        (eventParentRecord: RecordProxy) => {
          // TODO: do this smarter at some point
          const eventRecord = (eventParentRecord.getLinkedRecords("events") || [])[0];

          if (!eventRecord) {
            return;
          }

          const startTime = eventRecord.getLinkedRecord("startTime");
          const endTime = eventRecord.getLinkedRecord("endTime");

          if (!startTime || !endTime) {
            return;
          }

          const startDateOrDateTime: string = startTime.getValue("dateOrDateTime") as string;
          const endDateOrDateTime: string = endTime.getValue("dateOrDateTime") as string;

          let curr = new ZonedMoment(startDateOrDateTime);
          let weekStart = curr.clone().startOf("week").format("YYYY-MM-DD");

          weekDayMap[weekStart][curr.weekday()].add(eventParentRecord);

          while (!curr.isSame(endDateOrDateTime, "days")) {
            curr = curr.add(1, "days");
            weekStart = curr.clone().startOf("week").format("YYYY-MM-DD");

            weekDayMap[weekStart][curr.weekday()].add(eventParentRecord);
          }
        },
      );

      Object.keys(weekDayMap).forEach((weekStartDate) => {
        const byWeekProxy = orgProxy.getLinkedRecord("eventParentBaseWrapperByWeeksObject", {
          timeZone: input.timeZone, // TODO: what if this timezone changes between queries/updates?
          calendarIds: input.calendarIds,
          weekStartDates: [weekStartDate],
          includeAllDay: input.includeAllDay || false,
        });

        if (!byWeekProxy) {
          return;
        }

        weekDayMap[weekStartDate].forEach((eventParentSet, weekday) => {
          const eventParentBaseWrapperByWeek = (byWeekProxy.getLinkedRecords("list") || [])[0];
          if (!eventParentBaseWrapperByWeek) {
            return;
          }

          const eventParentBaseMap: { [key: string]: RecordProxy } = {};
          const eventParentMap: { [key: string]: RecordProxy } = {};

          const eventParentBases =
            eventParentBaseWrapperByWeek.getLinkedRecords("eventParentBases") || [];

          const eventParentsByDay: ReadonlyArray<RecordProxy | null> =
            eventParentBaseWrapperByWeek.getLinkedRecords("eventParentsByDay") || [];

          eventParentBases
            .concat(subscriptionField.getLinkedRecords("eventParentBases") || [])
            .forEach((eventParentBase) => {
              if (eventParentBase) {
                eventParentBaseMap[eventParentBase.getValue("id") as string] = eventParentBase;
              }
            });

          const eventParentsForWeekday = eventParentsByDay[weekday];

          if (eventParentsForWeekday) {
            (eventParentsForWeekday.getLinkedRecords("eventParents") || [])
              .concat(Array.from(eventParentSet))
              .forEach((eventParent) => {
                if (eventParent) {
                  eventParentMap[eventParent.getValue("id") as string] = eventParent;
                }
              });

            eventParentBaseWrapperByWeek.setLinkedRecords(
              Object.values(eventParentBaseMap),
              "eventParentBases",
            );
            eventParentsForWeekday.setLinkedRecords(Object.values(eventParentMap), "eventParents");
          }
        });
      });
    },
  });
}
